import { useState } from 'react';
import { arrayMove } from '@dnd-kit/sortable';
import { Column } from '@tanstack/react-table';

import { useTableContext } from '../../contexts';
import { getColumnDefIds } from '../../utils/getColumnDefIds';

export type TColumnSettingsItem = {
    id: string;
    label: string;
    visible: boolean;
};

const isConfigurable = (column: Column<any, unknown> | undefined): column is Column<any, unknown> =>
    !!column && column.getCanHide() && typeof column.columnDef.header === 'string';

export const useColumnSettingsDraft = () => {
    const table = useTableContext();

    const getItems = (columnIds: string[], getVisible: (column: Column<any, unknown>) => boolean) =>
        columnIds
            .map((id) => table.getColumn(id))
            .filter(isConfigurable)
            .map((column) => ({ id: column.id, label: String(column.columnDef.header), visible: getVisible(column) }));

    const [items, setItems] = useState<TColumnSettingsItem[]>(() =>
        getItems(table.getState().columnOrder, (column) => column.getIsVisible()),
    );
    const visibleCount = items.filter((item) => item.visible).length;

    const toggle = (id: string) => {
        setItems((prevItems) => {
            const item = prevItems.find((prevItem) => prevItem.id === id);
            if (item?.visible && prevItems.filter((prevItem) => prevItem.visible).length <= 1) return prevItems;

            return prevItems.map((prevItem) =>
                prevItem.id === id ? { ...prevItem, visible: !prevItem.visible } : prevItem,
            );
        });
    };

    const move = (activeId: string, overId: string) => {
        setItems((prevItems) => {
            const oldIndex = prevItems.findIndex((item) => item.id === activeId);
            const newIndex = prevItems.findIndex((item) => item.id === overId);
            return arrayMove(prevItems, oldIndex, newIndex);
        });
    };

    const reset = () => {
        setItems(getItems(getColumnDefIds(table.options.columns), () => true));
    };

    const save = () => {
        const visibility = Object.fromEntries(items.map((item) => [item.id, item.visible]));

        table.setColumnVisibility((prevVisibility) => ({ ...prevVisibility, ...visibility }));
        table.setColumnOrder(items.map((item) => item.id));
        table.setColumnFilters((prevFilters) => prevFilters.filter((filter) => visibility[filter.id] !== false));
    };

    return { items, visibleCount, toggle, move, reset, save };
};
